/**
 * 相机环境能力判定
 * -----------------------------------------------------------------------------
 * 为什么需要它：`<video>` 实时取景（`getUserMedia`）不是「有摄像头就能用」，
 * 它同时受**三个**与业务无关的条件约束，任一不满足就只剩系统相机一条路：
 *
 *   ① **安全上下文**（`https:` 或 `localhost`）。内网 IP + http 访问时，
 *      浏览器直接把 `navigator.mediaDevices` 置为 `undefined` —— 这不是权限被拒，
 *      也不是代码写错，而是规范层面的硬拦截。手机连 `http://192.168.x.x:5173`
 *      调试时必然命中，且**没有任何前端办法绕过**。
 *   ② **微信内置浏览器不支持取流**。iOS 微信走 WKWebView、Android 微信走 X5，
 *      两者都不给 `getUserMedia`（历史上从未稳定支持）。所以"只在微信里实现"
 *      这个方向是反的 —— 微信恰恰是取景最不可用的那个环境。
 *   ③ 浏览器本身没有 `getUserMedia`（老旧内核、部分国产套壳浏览器）。
 *
 * 三者都不满足时**仍然能真调相机**：`<input type="file" capture="environment">`
 * 会拉起系统相机，拍完回传一张成品图。它不依赖安全上下文，在微信里也有效。
 * 代价是没有实时取景画面，因此也就没有蒙版引导与按框裁切（成片只能靠服务端纠偏）。
 *
 * 所以本模块的产出不是「能不能拍」，而是「能走哪条路」：
 *   `canLiveCapture === true`  → 全屏取景 + 蒙版 + 按框裁切（体验最好）
 *   `canLiveCapture === false` → 单按钮降级：微信内「相册」（弹拍照 / 选图）、
 *                                非微信「系统相机」（真机可用，但没有取景引导）
 *
 * ⚠️ 判定**顺序**是 `微信 → 安全上下文 → 支持度`：微信里即便上了 https 也取不到流，
 * 若先报「非安全上下文」会把排查方向带偏（去折腾证书，而问题不在证书）。
 */

/** 为什么不能实时取景 */
export type CameraEnvReason = 'ok' | 'wechat' | 'insecure' | 'unsupported';

/** 判定输入（全部可注入，便于单测覆盖真机组合而不伪造全局环境） */
export interface CameraEnvInput {
  userAgent: string;
  /** `window.isSecureContext`；老浏览器没有该属性 */
  secureContext: boolean;
  hasGetUserMedia: boolean;
}

export interface CameraEnv extends CameraEnvInput {
  /** 微信（含企业微信）内置浏览器 */
  isWechat: boolean;
  isIos: boolean;
  isAndroid: boolean;
  isMobile: boolean;
  /** 能否开实时取景（`<video>` 取流 → 蒙版 → 按框裁切） */
  canLiveCapture: boolean;
  reason: CameraEnvReason;
}

/** 微信内置浏览器；企业微信 UA 里同样带 `MicroMessenger`，一并覆盖 */
const WECHAT_UA = /MicroMessenger/i;
const IOS_UA = /iPad|iPhone|iPod/i;
const ANDROID_UA = /Android/i;
/** 只要能拿 `Mobile` 端语义就够；iPadOS 13+ 伪装成 Macintosh，靠 `maxTouchPoints` 另判 */
const MOBILE_UA = /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i;

function readUserAgent(): string {
  return typeof navigator === 'undefined' ? '' : navigator.userAgent;
}

function readSecureContext(): boolean {
  // 老内核没有 `isSecureContext`：退回协议 + 主机名判定，与规范口径一致
  if (typeof window !== 'undefined' && typeof window.isSecureContext === 'boolean') {
    return window.isSecureContext;
  }
  if (typeof location === 'undefined') return false;
  const { protocol, hostname } = location;
  return protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1';
}

function readHasGetUserMedia(): boolean {
  return (
    typeof navigator !== 'undefined' && typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/**
 * 判定当前环境。参数全可省略 —— 省略项从真实浏览器读取，
 * 单测直接传 `{ userAgent, secureContext, hasGetUserMedia }` 即可。
 */
export function detectCameraEnv(overrides: Partial<CameraEnvInput> = {}): CameraEnv {
  const userAgent = overrides.userAgent ?? readUserAgent();
  const secureContext = overrides.secureContext ?? readSecureContext();
  const hasGetUserMedia = overrides.hasGetUserMedia ?? readHasGetUserMedia();

  const isWechat = WECHAT_UA.test(userAgent);
  const isIos = IOS_UA.test(userAgent);
  const isAndroid = ANDROID_UA.test(userAgent);
  // iPadOS 13+ 的 UA 与 macOS 相同，只能靠触点数区分
  const isIpadDesktopUa =
    /Macintosh/i.test(userAgent) &&
    typeof navigator !== 'undefined' &&
    (navigator.maxTouchPoints ?? 0) > 1;
  const isMobile = MOBILE_UA.test(userAgent) || isIpadDesktopUa;

  let reason: CameraEnvReason;
  if (isWechat) reason = 'wechat';
  else if (!secureContext) reason = 'insecure';
  else if (!hasGetUserMedia) reason = 'unsupported';
  else reason = 'ok';

  return {
    userAgent,
    secureContext,
    hasGetUserMedia,
    isWechat,
    isIos: isIos || isIpadDesktopUa,
    isAndroid,
    isMobile,
    canLiveCapture: reason === 'ok',
    reason,
  };
}

/**
 * 给学员看的降级说明（浮层里那一行字）。
 *
 * 刻意**不解释技术原因**：学员既不关心什么是安全上下文，也改不了它；
 * 说清「没有实时画面 + 下一步点哪个按钮」就够了。
 * 真实原因（`reason`）留给排查用，挂在浮层的 `data-reason` 上。
 *
 * 「怎么拍」不写在这里：浮层里有一张**静态示例蒙版**（`camera-overlay__demo`）
 * 加一句构图要求（`FALLBACK_TIP`），比一句「请拍摄」有用得多（2026-10-26 真机反馈）。
 *
 * ⚠️ 文案里**不点按钮名**（尤其微信那句）：微信内那个位置的按钮叫「相册」，
 * 非微信叫「系统相机」，把按钮名写死在这句话里必然有一边指空。
 * 按钮自己的解释由 `CameraCapture` 的 `camera-overlay__note` 承担。
 */
export function describeLiveCaptureFallback(reason: CameraEnvReason): string {
  switch (reason) {
    case 'wechat':
      // 微信内那个按钮叫「相册」，点它会弹「拍照 / 从相册选择」——
      // 所以这句话说「点下方按钮可拍照或选已有照片」，不能写「用手机相机拍摄」
      // （那样学员会去找一个并不存在的相机入口）
      return '微信内不支持实时取景；点下方按钮可拍照或选择已有照片';
    case 'insecure':
      return '当前访问方式不支持实时取景，点击「系统相机」将打开手机相机拍摄';
    case 'unsupported':
      return '当前浏览器不支持实时取景，点击「系统相机」将打开手机相机拍摄';
    default:
      return '';
  }
}

/** 降级形态下那唯一一个按钮：文案 + 提示语 + 该触发哪个 `<input>` */
export interface FallbackAction {
  /** 按钮文案 */
  label: string;
  /** 按钮下方那行小字（说清点下去会发生什么） */
  note: string;
  /** `album` = 带 `accept` 的选择器（微信里会弹「拍照 / 从相册选择」）；`camera` = `capture` 直进相机 */
  target: 'album' | 'camera';
}

/**
 * 判定降级形态下的**唯一**动作。
 *
 * 规则只有三条，顺序不能换：
 *   1. `allowAlbum === false` → 永远 `camera`。这是一个**强制现拍的能力位**，
 *      留给「口径要求必须当场拍」的场景。当前没有调用方用它（2026-10-26 起
 *      职业证件照也允许从相册选图，见 `KycFlow` 里的裁决说明），但保留它：
 *      将来若某个证件类影像被要求现拍，改的是调用点的 props，不是这里的判定。
 *   2. 微信内 + 允许相册 → 「相册」。微信点带 `accept` 的 input 会弹
 *      「拍照 / 从相册选择」两个选项，**一个按钮就覆盖了系统相机与相册**，
 *      再并排一个「系统相机」只是把同一个入口说两遍。
 *   3. 其余（非微信降级）→ 「系统相机」。非微信没有那个合并菜单，
 *      `accept` 会直接进图库，想让用户拍照就得靠 `capture`。
 */
export function resolveFallbackAction(input: {
  isWechat: boolean;
  allowAlbum: boolean;
  /** 相机是唯一一条路时，按钮用拍摄语义（「立即拍摄 / 重新拍摄」）比用设备名更好懂 */
  actionText: string;
}): FallbackAction {
  if (!input.allowAlbum) {
    return { label: input.actionText, note: '仅支持相机实拍，不可从相册选择', target: 'camera' };
  }
  if (input.isWechat) {
    return {
      label: '相册',
      note: '点击会弹出「拍照」与「从相册选择」两个选项',
      target: 'album',
    };
  }
  return { label: '系统相机', note: '点击后打开手机相机，拍完自动返回', target: 'camera' };
}
